import type { DashboardData, WeekDay } from '@shared/types/progress'

/** The dashboard's building blocks. Presentational only — no data fetching. */

export function CountdownCard({ trip }: { trip: DashboardData['trip'] }) {
  if (trip.daysUntil === null) return null

  const arrived = trip.daysUntil === 0

  return (
    <section className="overflow-hidden rounded-card bg-brand-600 p-7 text-center text-white shadow-sm">
      <p className="text-sm font-medium tracking-[0.18em] text-brand-100 uppercase">
        {arrived ? 'Você chegou' : 'Brazil in'}
      </p>
      {arrived ? (
        <p className="mt-2 font-display text-4xl leading-tight font-bold">Boa viagem!</p>
      ) : (
        <>
          <p className="font-display text-7xl leading-none font-bold tabular-nums">
            {trip.daysUntil}
          </p>
          <p className="mt-1 font-medium text-brand-100">
            {trip.daysUntil === 1 ? 'day' : 'days'}
          </p>
        </>
      )}
      <span aria-hidden="true" className="mx-auto mt-4 block h-px w-12 bg-gold-500/70" />
      {trip.date && (
        <p className="mt-4 text-sm text-brand-100">{formatTripDate(trip.date)}</p>
      )}
    </section>
  )
}

function formatTripDate(date: string): string {
  // Parsed at UTC noon so the displayed date can never slip a day backwards
  // in a western timezone.
  return new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

export function MissionCard({
  mission,
  onStart,
}: {
  mission: DashboardData['mission']
  onStart: (lessonId: string) => void
}) {
  if (!mission) {
    return (
      <section className="rounded-card bg-white p-5 text-center ring-1 ring-brand-900/5">
        <p className="text-3xl" aria-hidden="true">
          🎉
        </p>
        <h2 className="mt-2 font-display text-xl font-bold">Every lesson done</h2>
        <p className="mt-1 text-sm text-ink-500">
          More is on the way. Reviewing what you know is never wasted.
        </p>
      </section>
    )
  }

  return (
    <section className="rounded-card bg-white p-5 shadow-sm ring-1 ring-brand-900/5">
      <p className="text-xs font-semibold tracking-[0.16em] text-brand-500 uppercase">
        Today&rsquo;s mission
      </p>
      <h2 className="mt-1.5 font-display text-2xl leading-tight font-bold text-ink-900">
        {mission.title}
      </h2>
      {mission.objective && <p className="mt-1.5 text-ink-700">{mission.objective}</p>}
      <p className="mt-2 text-sm text-ink-500">
        {mission.unitTitle} · about {mission.estimatedMin} min
      </p>
      <button
        type="button"
        onClick={() => onStart(mission.lessonId)}
        className="mt-4 flex min-h-13 w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-5 font-semibold text-white transition-colors hover:bg-brand-700 active:bg-brand-800"
      >
        {mission.resuming ? 'Continue lesson' : 'Start lesson'}
        <span aria-hidden="true">→</span>
      </button>
    </section>
  )
}

/**
 * The streak, the daily goal ring, and the week strip in one card — they are
 * three views of the same question ("am I keeping this up?").
 */
export function StreakCard({
  streak,
  goal,
  week,
}: {
  streak: DashboardData['streak']
  goal: DashboardData['goal']
  week: WeekDay[]
}) {
  const pct = Math.min(100, Math.round((goal.xp / goal.goalXp) * 100))

  return (
    <section className="rounded-card bg-white p-5 shadow-sm ring-1 ring-brand-900/5">
      <div className="flex items-center gap-4">
        <GoalRing pct={pct} />
        <div className="min-w-0 flex-1">
          <p className="font-display text-xl font-bold text-ink-900">
            {streak.current > 0
              ? `${streak.current} day${streak.current === 1 ? '' : 's'} in a row`
              : 'Start your streak'}
          </p>
          <p className="mt-0.5 text-sm text-ink-500">
            {goal.met
              ? "Today's goal is done. 🔥"
              : `${goal.remaining} XP to today's goal`}
          </p>
          {streak.current > 0 && !streak.extendedToday && (
            <p className="mt-1 text-sm font-medium text-gold-700">
              Practise today to keep it alive.
            </p>
          )}
        </div>
      </div>

      <ul className="mt-5 flex justify-between gap-1">
        {week.map((day) => (
          <li key={day.date} className="flex flex-1 flex-col items-center gap-1.5">
            <span
              aria-hidden="true"
              className={`flex size-9 items-center justify-center rounded-full text-sm font-semibold ${dayTone(day)}`}
            >
              {day.goalMet ? '✓' : day.active ? '·' : ''}
            </span>
            <span
              className={`text-xs ${day.isToday ? 'font-bold text-ink-900' : 'text-ink-400'}`}
            >
              {day.letter}
            </span>
            <span className="sr-only">
              {day.date}: {day.goalMet ? 'goal met' : day.active ? 'practised' : 'no practice'}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

function dayTone(day: WeekDay): string {
  // The ring is applied independently of the fill: today must stand out
  // whether or not she has practised yet, and an `if` chain would let the
  // "active" branch swallow it.
  const ring = day.isToday ? ' ring-2 ring-brand-600 ring-offset-1' : ''
  if (day.goalMet) return `bg-brand-600 text-white${ring}`
  if (day.active) return `bg-brand-200 text-brand-700${ring}`
  return `bg-sand-100 text-ink-400${ring}`
}

function GoalRing({ pct }: { pct: number }) {
  const radius = 26
  const circumference = 2 * Math.PI * radius
  const offset = circumference * (1 - pct / 100)

  return (
    <div className="relative shrink-0">
      <svg width="68" height="68" viewBox="0 0 68 68" aria-hidden="true">
        <circle
          cx="34"
          cy="34"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="7"
          className="text-sand-200"
        />
        <circle
          cx="34"
          cy="34"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          // Rotated so progress starts at twelve o'clock.
          transform="rotate(-90 34 34)"
          className="text-gold-500 transition-[stroke-dashoffset] duration-500"
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-ink-900 tabular-nums">
        {pct}%
      </span>
    </div>
  )
}

export function StatsGrid({ stats }: { stats: DashboardData['stats'] }) {
  return (
    <section className="grid grid-cols-2 gap-3">
      <Stat
        label="Brazil readiness"
        value={`${stats.readinessPct}%`}
        hint="Lessons and words together"
        tone="gold"
      />
      <Stat
        label="Lessons done"
        value={`${stats.lessonsCompleted}`}
        hint={`of ${stats.lessonsTotal}`}
      />
      {/* Two different numbers, deliberately: how many words she has met, and
          how many have actually stuck. Showing only the first would flatter
          her; showing only the second would look like no progress at all in
          the first week, before anything has had time to mature. */}
      <Stat
        label="Words learned"
        value={`${stats.wordsLearned}`}
        hint={`of ${stats.wordsTotal}`}
      />
      <Stat
        label="Words mastered"
        value={`${stats.wordsRetained}`}
        hint="Held in memory"
      />
      <Stat label="Total XP" value={`${stats.totalXp}`} hint="Since day one" />
    </section>
  )
}

function Stat({
  label,
  value,
  hint,
  tone = 'plain',
}: {
  label: string
  value: string
  hint?: string
  tone?: 'plain' | 'gold'
}) {
  return (
    <div
      className={`rounded-card p-4 ring-1 ring-brand-900/5 ${
        tone === 'gold' ? 'bg-cream' : 'bg-white'
      }`}
    >
      <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">{label}</p>
      <p className="mt-1 font-display text-3xl leading-none font-bold text-ink-900 tabular-nums">
        {value}
      </p>
      {hint && <p className="mt-1 text-xs text-ink-400">{hint}</p>}
    </div>
  )
}

export function WeakAreasCard({ areas }: { areas: DashboardData['weakAreas'] }) {
  if (areas.length === 0) {
    return (
      <section className="rounded-card bg-sky-soft p-5">
        <h2 className="font-display text-lg font-bold text-ink-900">Where you stand</h2>
        <p className="mt-1 text-sm text-ink-700">
          Answer a few more questions and this will show what to practise next.
        </p>
      </section>
    )
  }

  return (
    <section className="rounded-card bg-white p-5 shadow-sm ring-1 ring-brand-900/5">
      <h2 className="font-display text-lg font-bold text-ink-900">Worth another look</h2>
      <ul className="mt-3 space-y-3">
        {areas.map((area) => (
          <li key={area.category}>
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-medium text-ink-900">{area.label}</span>
              <span className="text-sm text-ink-500 tabular-nums">{area.accuracyPct}%</span>
            </div>
            <div
              className="mt-1.5 h-2 overflow-hidden rounded-full bg-sand-100"
              role="progressbar"
              aria-valuenow={area.accuracyPct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${area.label} accuracy`}
            >
              <div
                className={`h-full rounded-full ${
                  area.accuracyPct >= 80
                    ? 'bg-brand-500'
                    : area.accuracyPct >= 60
                      ? 'bg-gold-500'
                      : 'bg-clay-500'
                }`}
                style={{ width: `${Math.max(4, area.accuracyPct)}%` }}
              />
            </div>
            {area.weakestSkill && (
              <p className="mt-1 text-xs text-ink-400">
                Weakest: {area.weakestSkill.label} ({area.weakestSkill.accuracyPct}%)
              </p>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-4 text-xs text-ink-400">
        Based on your first answer to each question.
      </p>
    </section>
  )
}
